import React from "react";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { Link } from "react-router-dom";

interface AuditMetric {
  label: string;
  value?: number | null;
  format?: "number" | "percent";
}

interface AuditSection {
  title: string;
  path: string;
  metrics: AuditMetric[];
}

interface AuditCardProps {
  section: AuditSection;
}

const formatMetricValue = (metric: AuditMetric): string => {
  if (metric.value === null || metric.value === undefined) {
    return "";
  }

  if (metric.format === "percent") {
    return `${metric.value.toFixed(1)}%`;
  }

  return metric.value.toLocaleString();
};

const AuditCard: React.FC<AuditCardProps> = ({ section }) => {
  const header = (
    <div className="text-lg font-semibold text-gray-800">{section.title}</div>
  );

  return (
    <Link
      to={section.path}
      style={{ textDecoration: "none" }}
      aria-label={`${section.title} details`}
    >
      <Card className="h-full" header={header}>
        <div className="flex flex-column gap-3">
          {section.metrics.map((metric, index) => {
            const showDivider = index < section.metrics.length - 1;
            return (
              <div
                key={metric.label}
                className={`flex align-items-center justify-content-between py-2 ${
                  showDivider ? "border-bottom-1 surface-border" : ""
                }`}
              >
                <div className="text-sm text-gray-600">{metric.label}</div>
                <div
                  className="text-xl font-medium text-gray-900"
                  style={{ minWidth: "6rem", textAlign: "right" }}
                >
                  {metric.value === null || metric.value === undefined ? (
                    <Skeleton width="6rem" height="1.25rem"></Skeleton>
                  ) : (
                    formatMetricValue(metric)
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-3 text-right text-blue-500">View details →</div>
      </Card>
    </Link>
  );
};

const MusicCMSConsoleDashboard: React.FC = () => {
  const sections: AuditSection[] = [
    {
      title: "Sound Recording Audit",
      path: "/audit/sound-recording",
      metrics: [
        { label: "Total Items", value: 33, format: "number" },
        { label: "Pass Rate", value: 99.6, format: "percent" },
        { label: "Issues Found", value: 50, format: "number" },
      ],
    },
    {
      title: "Composition Audit",
      path: "/audit/composition",
      metrics: [
        { label: "Total Items", value: 12500, format: "number" },
        { label: "Pass Rate", value: 95.5, format: "percent" },
        { label: "Issues Found", value: 300, format: "number" },
      ],
    },
    {
      title: "Asset Type Audit",
      path: "/audit/asset-type",
      metrics: [
        { label: "Total Items", value: 15000, format: "number" },
        { label: "Pass Rate", value: 99.8, format: "percent" },
        { label: "Issues Found", value: 10, format: "number" },
      ],
    },
    {
      title: "DSR Audits",
      path: "/audit/dsr",
      metrics: [
        { label: "Total Items", value: 8000, format: "number" },
        { label: "Pass Rate", value: 85.0, format: "percent" },
        { label: "Issues Found", value: 1200, format: "number" },
      ],
    },
  ];

  return (
    <div className="p-4" style={{ backgroundColor: "#f4f7fa" }}>
      <h2 className="text-2xl font-bold mb-4 text-gray-800">
        Music CMS Console
      </h2>
      <div className="grid">
        {sections.map((section) => (
          <div key={section.title} className="col-12 md:col-6 xl:col-3 p-2">
            <div className="h-full">
              <AuditCard section={section} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MusicCMSConsoleDashboard;
